import {
  ArrowCircleRightIcon,
  PlusSquareIcon,
  QuestionIcon,
  XIcon,
} from "@phosphor-icons/react";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  isOutOfIterations,
  useBillingStatus,
} from "../components/BillingStatusContext";
import { Button } from "../components/Button";
import { Header } from "../components/Header";
import { HelpfulTipsModal } from "../components/HelpfulTipsModal";
import type { AudioInputStatus } from "../components/home/VoiceInputButtons";
import { VoiceInputButtons } from "../components/home/VoiceInputButtons";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { OutOfIterationsOverlay } from "../components/OutOfIterations";
import { ReferenceImageModal } from "../components/ReferenceImageModal";
import { ReferenceImagePill } from "../components/ReferenceImagePill";
import {
  type AudioState,
  type AuthState,
  type DropZoneState,
  type GenerationState,
  type InputState,
  useHomePage,
} from "../hooks/useHomePage";
import { SUPPORTED_IMAGE_TYPES } from "../hooks/useImageDropZone";
import {
  type RefineUploadState,
  useRefineUpload,
} from "../hooks/useRefineUpload";
import { MAX_REFERENCE_IMAGES } from "../lib/image-constants";
import { homeSearchSchema } from "../lib/route-schemas";

export const Route = createFileRoute("/")({
  validateSearch: homeSearchSchema,
  component: HomePage,
});

type DesignMode = "refine" | "create";

const MODE_OPTIONS = [
  {
    value: "refine",
    title: "Refine Existing",
    description:
      "Start here if you already have an image and want to make edits.",
  },
  {
    value: "create",
    title: "Create New",
    description:
      "Start here if you're exploring ideas and want to generate new images.",
  },
] as const;

interface ModeToggleProps {
  mode: DesignMode;
  onModeChange: (mode: DesignMode) => void;
  disabled: boolean;
}

function ModeToggle({ mode, onModeChange, disabled }: ModeToggleProps) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 bg-[var(--bg-default)] rounded-xl p-1.5 mb-4">
      {MODE_OPTIONS.map((option) => {
        const isSelected = mode === option.value;
        return (
          <button
            key={option.value}
            type="button"
            onClick={() => onModeChange(option.value)}
            disabled={disabled}
            aria-pressed={isSelected}
            className={`rounded-lg p-3.5 text-left transition-colors cursor-pointer disabled:cursor-not-allowed ${
              isSelected
                ? "bg-[var(--accent-primary)]"
                : "hover:bg-[var(--bg-canvas)]"
            }`}
          >
            <span className="block font-semibold text-[var(--text-primary)]">
              {option.title}
            </span>
            <span
              className={`block text-sm mt-1 ${
                isSelected
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-secondary)]"
              }`}
            >
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function RefinePreview({
  refine,
  zoneStyles,
}: {
  refine: RefineUploadState;
  zoneStyles: string;
}) {
  return (
    <>
      <div className={`${zoneStyles} relative p-4`}>
        <img
          src={refine.previewUrl ?? undefined}
          alt="Selected design to refine"
          className="max-h-64 max-w-full rounded-lg"
        />
        <button
          type="button"
          onClick={refine.handleRemove}
          disabled={refine.isUploading}
          aria-label="Remove selected image"
          className="absolute top-3 right-3 p-1.5 rounded-full bg-[var(--bg-modal)] text-[var(--text-primary)] hover:opacity-80 transition-opacity cursor-pointer disabled:cursor-not-allowed"
        >
          <XIcon size={16} />
        </button>
      </div>
      <p className="mt-2 text-xs text-center text-[var(--text-secondary)]">
        This image will be resized to 3:2 after the first generation.
      </p>
    </>
  );
}

function RefineUploadBody({ refine }: { refine: RefineUploadState }) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);

  const zoneStyles = `w-full min-h-40 rounded-xl border border-dashed flex items-center justify-center transition-colors ${
    isDraggingOver
      ? "border-[var(--accent-primary)] bg-[var(--bg-default)]"
      : "border-[var(--stroke-field)]"
  }`;

  // Wrap the drop-zone handlers to drive the drag highlight (the hook's
  // built-in highlight only targets textareas).
  const dropZoneProps = {
    ...refine.dropZoneProps,
    onDragOver: (e: React.DragEvent) => {
      setIsDraggingOver(true);
      refine.dropZoneProps.onDragOver(e);
    },
    onDragLeave: (e: React.DragEvent) => {
      setIsDraggingOver(false);
      refine.dropZoneProps.onDragLeave(e);
    },
    onDrop: (e: React.DragEvent) => {
      setIsDraggingOver(false);
      refine.dropZoneProps.onDrop(e);
    },
  };

  return (
    <div {...dropZoneProps}>
      <input
        ref={refine.fileInputRef}
        type="file"
        accept={SUPPORTED_IMAGE_TYPES.join(",")}
        onChange={refine.handleFileInputChange}
        className="hidden"
        disabled={refine.isUploading}
      />
      {refine.previewUrl ? (
        <RefinePreview refine={refine} zoneStyles={zoneStyles} />
      ) : (
        <button
          type="button"
          onClick={() => refine.fileInputRef.current?.click()}
          disabled={refine.isUploading}
          className={`${zoneStyles} relative gap-2 text-[var(--text-secondary)] hover:bg-[var(--bg-default)] cursor-pointer disabled:cursor-not-allowed`}
        >
          <PlusSquareIcon size={18} />
          Upload Your Image
          <span className="absolute bottom-3 inset-x-4 text-xs text-center">
            Uploaded images may be resized or extended to fit the required 3:2
            aspect ratio for generation.
          </span>
        </button>
      )}
    </div>
  );
}

interface PromptTextAreaProps {
  input: InputState;
  audio: AudioState;
  isInputDisabled: boolean;
  dropZone: DropZoneState;
  projectId?: string;
}

function PromptTextArea({
  input,
  audio,
  isInputDisabled,
  dropZone,
  projectId,
}: PromptTextAreaProps) {
  const audioInputStatus: AudioInputStatus = {
    isGenerating: isInputDisabled,
    isTranscribing: audio.isTranscribing,
    isListening: audio.isListening,
    isSpeechSupported: audio.isSpeechSupported,
  };
  const uploadDisabled =
    isInputDisabled || dropZone.references.length >= MAX_REFERENCE_IMAGES;
  return (
    <div className="bg-[var(--bg-default)] rounded-xl p-4 relative">
      <div className="relative" {...dropZone.dropZoneProps}>
        <textarea
          value={input.prompt}
          onChange={(e) => input.setPrompt(e.target.value)}
          onKeyDown={input.handleKeyDown}
          placeholder=""
          className="w-full bg-transparent border-none outline-none resize-none text-xl text-[var(--text-primary)] placeholder:text-[var(--text-secondary)] transition-all duration-200"
          rows={4}
          disabled={isInputDisabled || audio.isTranscribing}
        />
        <div className="flex flex-wrap items-center gap-3 pt-2 pr-20">
          <button
            type="button"
            onClick={dropZone.openReferenceModal}
            disabled={uploadDisabled}
            aria-label="Add reference image"
            className="text-[var(--text-secondary)] hover:text-[var(--accent-primary)] transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <PlusSquareIcon className="h-4 w-4" />
          </button>
          {dropZone.references.map((img, i) => (
            <ReferenceImagePill
              key={img.previewUrl}
              imageIndex={i}
              imageDataUrl={img.previewUrl}
              assetName={img.assetName}
              onRemove={() => dropZone.handleReferenceImageRemove(i)}
              disabled={isInputDisabled}
            />
          ))}
        </div>
        <VoiceInputButtons
          audio={audioInputStatus}
          audioInputRef={audio.audioInputRef}
          onAudioUpload={audio.handleAudioUpload}
          onToggleSpeech={audio.toggleSpeech}
        />
      </div>
      <ReferenceImageModal
        isOpen={dropZone.isReferenceModalOpen}
        initialFile={dropZone.incomingFile}
        projectId={projectId}
        onSelect={dropZone.addReference}
        onClose={dropZone.closeReferenceModal}
      />
    </div>
  );
}

interface PromptCardProps {
  error: string | null;
  busyText: string | null;
  onShowTips: () => void;
  children: React.ReactNode;
}

function PromptCard({
  error,
  busyText,
  onShowTips,
  children,
}: PromptCardProps) {
  return (
    <div className="bg-[var(--bg-modal)] rounded-2xl p-6">
      <div className="flex flex-col">
        <div className="flex items-center justify-between">
          <p className="text-xs font-medium uppercase tracking-[0.15em] text-[var(--text-secondary)]">
            Get Started
          </p>
          <button
            onClick={onShowTips}
            className="cursor-pointer hover:opacity-80 transition-opacity"
            type="button"
            aria-label="Show helpful tips"
          >
            <QuestionIcon
              size={20}
              className="text-[var(--accent-underline-fill)]"
            />
          </button>
        </div>
        <h2 className="text-heading-modal pt-2 pb-4">
          What Are You Designing?
        </h2>
      </div>
      {children}
      {error && (
        <div className="mt-6 p-4 bg-[var(--accent-destructive)] border border-[var(--stroke-destructive)] rounded-lg text-[var(--text-primary)]">
          <p className="font-medium">Error:</p>
          <p className="text-sm mt-1">{error}</p>
        </div>
      )}
      {busyText && (
        <div className="mt-8">
          <LoadingSpinner text={busyText} size="md" />
        </div>
      )}
    </div>
  );
}

interface HomeFooterProps {
  mode: DesignMode;
  refine: RefineUploadState;
  generation: GenerationState;
  isInputDisabled: boolean;
  hasPrompt: boolean;
}

function HomeFooter({
  mode,
  refine,
  generation,
  isInputDisabled,
  hasPrompt,
}: HomeFooterProps) {
  return (
    <div className="mt-6 flex items-center justify-between gap-4">
      <p className="text-sm text-[var(--text-secondary)]">
        For guidance, check out Designer Solutions{" "}
        <a
          href="https://www.iqdesign.ai/workflow-library"
          target="_blank"
          rel="noopener noreferrer"
          className="text-modal-support-link hover:underline"
        >
          iqdesign.ai/workflow-library
        </a>
      </p>
      {mode === "refine" ? (
        <Button
          onClick={refine.handleRefine}
          disabled={!refine.hasFile || refine.isUploading}
        >
          {refine.isUploading ? "Preparing your design..." : "Refine"}
          <ArrowCircleRightIcon size={18} />
        </Button>
      ) : (
        <Button
          onClick={generation.handleGenerate}
          disabled={isInputDisabled || !hasPrompt}
        >
          {isInputDisabled ? "Starting generation..." : "Create"}
          <ArrowCircleRightIcon size={18} />
        </Button>
      )}
    </div>
  );
}

function getBusyText(
  mode: DesignMode,
  isUploading: boolean,
  isGenerating: boolean,
): string | null {
  if (mode === "refine") {
    return isUploading ? "Preparing your design..." : null;
  }
  return isGenerating ? "Starting image generation..." : null;
}

function HomePage() {
  const { projectId } = Route.useSearch();
  const [isHelpfulTipsOpen, setIsHelpfulTipsOpen] = useState(false);
  const [mode, setMode] = useState<DesignMode>("refine");
  const { input, generation, audio, dropZone, auth } = useHomePage(projectId);
  const refine = useRefineUpload(projectId);
  const { billingStatus, isBillingLoading } = useBillingStatus();
  const outOfIterations = isOutOfIterations(billingStatus, isBillingLoading);

  if (auth.isLoading || auth.authError) {
    return <HomeLoadingState auth={auth} />;
  }

  const isInputDisabled =
    generation.isGenerating || audio.isTranscribing || audio.isListening;
  const isBusy = mode === "refine" ? refine.isUploading : isInputDisabled;
  const hasPrompt = input.prompt.trim().length > 0;

  return (
    <HomeContent
      mode={mode}
      onModeChange={setMode}
      input={input}
      generation={generation}
      audio={audio}
      dropZone={dropZone}
      refine={refine}
      isInputDisabled={isInputDisabled}
      isBusy={isBusy}
      hasPrompt={hasPrompt}
      outOfIterations={outOfIterations}
      projectId={projectId}
      tips={{
        isOpen: isHelpfulTipsOpen,
        onShow: () => setIsHelpfulTipsOpen(true),
        onClose: () => setIsHelpfulTipsOpen(false),
      }}
    />
  );
}

interface HomeLoadingStateProps {
  auth: AuthState;
}

function HomeLoadingState({ auth }: HomeLoadingStateProps) {
  if (auth.authError) {
    return (
      <div className="p-8 min-h-screen bg-[var(--bg-default)]">
        <h1 className="text-xl font-bold text-[var(--accent-destructive)]">
          Authentication Required
        </h1>
        <p className="mt-2 text-[var(--text-secondary)]">
          Please refresh the page to authenticate.
        </p>
      </div>
    );
  }
  return (
    <div className="p-8 min-h-screen bg-[var(--bg-default)]">
      <LoadingSpinner text="Loading..." size="md" />
    </div>
  );
}

interface HomeContentProps {
  mode: DesignMode;
  onModeChange: (mode: DesignMode) => void;
  input: InputState;
  generation: GenerationState;
  audio: AudioState;
  dropZone: DropZoneState;
  refine: RefineUploadState;
  isInputDisabled: boolean;
  isBusy: boolean;
  hasPrompt: boolean;
  outOfIterations: boolean;
  projectId?: string;
  tips: { isOpen: boolean; onShow: () => void; onClose: () => void };
}

function HomeContent({
  mode,
  onModeChange,
  input,
  generation,
  audio,
  dropZone,
  refine,
  isInputDisabled,
  isBusy,
  hasPrompt,
  outOfIterations,
  projectId,
  tips,
}: HomeContentProps) {
  return (
    <div className="min-h-screen bg-[var(--bg-default)] flex flex-col">
      <Header currentPage="home" />
      <main className="flex-1 flex px-8 lg:px-16 pt-4 pb-10">
        <div className="relative flex flex-1 rounded-xl overflow-hidden">
          {/* While the overlay is up the workspace behind it is inert: the
              overlay swallows mouse clicks, and inert + aria-hidden keeps
              keyboard and screen-reader users off the controls behind it. The
              outer Header stays reachable. */}
          <div
            inert={outOfIterations}
            aria-hidden={outOfIterations}
            className="flex flex-1"
          >
            {/* Collapsed drawer: becomes the parameter/prompt panel on the
                refine and results screens. */}
            <div
              className="w-16 lg:w-[72px] shrink-0 bg-[var(--bg-modal)]"
              aria-hidden="true"
            />
            <div className="flex-1 bg-[var(--bg-canvas)] flex items-center justify-center px-6 py-10">
              <div className="w-full max-w-[640px]">
                <PromptCard
                  error={mode === "refine" ? refine.error : generation.error}
                  busyText={getBusyText(
                    mode,
                    refine.isUploading,
                    generation.isGenerating,
                  )}
                  onShowTips={tips.onShow}
                >
                  <ModeToggle
                    mode={mode}
                    onModeChange={onModeChange}
                    disabled={isBusy}
                  />
                  {mode === "refine" ? (
                    <RefineUploadBody refine={refine} />
                  ) : (
                    <PromptTextArea
                      input={input}
                      audio={audio}
                      isInputDisabled={isInputDisabled}
                      dropZone={dropZone}
                      projectId={projectId}
                    />
                  )}
                </PromptCard>
                <HomeFooter
                  mode={mode}
                  refine={refine}
                  generation={generation}
                  isInputDisabled={isInputDisabled}
                  hasPrompt={hasPrompt}
                />
                <HelpfulTipsModal isOpen={tips.isOpen} onClose={tips.onClose} />
              </div>
            </div>
          </div>
          {outOfIterations && <OutOfIterationsOverlay />}
        </div>
      </main>
    </div>
  );
}
